/**
 * Mock Mapping Service
 * Simulates Multi-Agent Mapping System API calls with realistic delays
 * Uses mockAiResponses.json for configuration and response templates
 */

import mockAiResponses from '../data/mockAiResponses.json';
import omopVocabulary from '../data/omopVocabulary.json';

/**
 * Simulates a delay (for realistic API behavior)
 * @param {number} ms - Milliseconds to delay
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Stage callback for progress updates during AI mapping generation
 * @callback StageCallback
 * @param {string} stageName - Name of the current stage
 * @param {string} status - Status of the stage (running, completed, error)
 * @param {object} data - Stage-specific data
 */

/**
 * Generate AI mapping for a parameter
 * Simulates the Multi-Agent System pipeline (Agent 1 → 2 → 3)
 *
 * @param {object} parameter - Parameter object to generate mapping for
 * @param {StageCallback} onStageUpdate - Optional callback for stage progress updates
 * @returns {Promise<object>} - Generated mapping result with aiMetadata
 */
export const generateAiMapping = async (parameter, onStageUpdate = null) => {
  const config = mockAiResponses.generateMapping;

  try {
    // Determine scenario based on parameter metadata quality
    let scenario = 'mediumConfidence';
    if (parameter.tags && parameter.tags.length > 3 && parameter.description) {
      scenario = 'highConfidence';
    } else if (!parameter.description || parameter.tags?.length < 2) {
      scenario = 'lowConfidence';
    }

    const scenarioData = config.scenarios[scenario];

    // Execute stages with callbacks
    for (const stage of config.stages) {
      if (onStageUpdate) {
        onStageUpdate(stage.name, 'running', { displayName: stage.displayName, description: stage.description });
      }

      await delay(stage.duration);

      if (onStageUpdate) {
        onStageUpdate(stage.name, 'completed', { displayName: stage.displayName });
      }
    }

    // Build response with scenario data
    const response = {
      status: 'success',
      omopMapping: {
        status: 'mapped',
        topMatch: scenarioData.agent3Output.allCandidates[0] ? {
          conceptId: scenarioData.agent3Output.allCandidates[0].conceptId,
          conceptCode: scenarioData.agent3Output.allCandidates[0].conceptCode,
          conceptName: scenarioData.agent3Output.allCandidates[0].conceptName,
          vocabulary: scenarioData.agent3Output.allCandidates[0].vocabulary,
          domain: scenarioData.agent3Output.allCandidates[0].domain
        } : null,
        alternatives: scenarioData.agent3Output.allCandidates.slice(1, 4).map(candidate => ({
          conceptId: candidate.conceptId,
          conceptCode: candidate.conceptCode,
          conceptName: candidate.conceptName,
          vocabulary: candidate.vocabulary,
          domain: candidate.domain,
          similarityScore: candidate.score
        })),
        aiMetadata: {
          confidenceScore: scenarioData.confidenceScore,
          agentRanking: scenarioData.agentRanking,
          totalCandidates: scenarioData.totalCandidates,
          agent1Output: scenarioData.agent1Output,
          agent2Output: scenarioData.agent2Output,
          agent3Output: scenarioData.agent3Output
        },
        validationHistory: [],
        criticHistory: []
      }
    };

    return response;
  } catch (error) {
    return {
      status: 'error',
      error: mockAiResponses.apiErrors.serverError
    };
  }
};

/**
 * Submit feedback to Critic Agent for iterative refinement
 *
 * @param {number} parameterId - Parameter ID
 * @param {string} feedback - User feedback text
 * @param {object} currentMapping - Current mapping data
 * @returns {Promise<object>} - Updated mapping with new ranking
 */
export const submitCriticFeedback = async (parameterId, feedback, currentMapping) => {
  const config = mockAiResponses.criticAgentIteration;

  try {
    // Simulate stage progression
    for (const stage of config.stages) {
      await delay(stage.duration);
    }

    // Build critic iteration response
    const iteration = {
      ...config.responseTemplate,
      iteration: (currentMapping.criticHistory?.length || 0) + 1,
      user: 'Current User', // TODO: Get from auth context
      feedback: feedback,
      timestamp: new Date().toISOString(),
      agentResponse: 'Re-ranking based on user feedback. Adjusting weights for specified criteria...',
      modificationsApplied: [
        'Increased weight for user-specified criteria',
        'Adjusted semantic similarity threshold',
        'Re-evaluated candidate ranking'
      ]
    };

    // Simulate re-ranking: swap top match with first alternative
    const alternatives = currentMapping.alternatives || [];
    if (alternatives.length > 0) {
      const previousTopMatch = {
        conceptId: currentMapping.topMatch.conceptId,
        conceptCode: currentMapping.topMatch.conceptCode,
        conceptName: currentMapping.topMatch.conceptName,
        confidenceScore: currentMapping.aiMetadata.confidenceScore
      };

      const newTopMatch = {
        conceptId: alternatives[0].conceptId,
        conceptCode: alternatives[0].conceptCode,
        conceptName: alternatives[0].conceptName,
        vocabulary: alternatives[0].vocabulary,
        confidenceScore: alternatives[0].similarityScore || 0.9,
        reason: 'Re-ranked based on user feedback regarding specific criteria'
      };

      iteration.previousTopMatch = previousTopMatch;
      iteration.newTopMatch = newTopMatch;

      // Update mapping
      const updatedMapping = {
        ...currentMapping,
        topMatch: {
          conceptId: newTopMatch.conceptId,
          conceptCode: newTopMatch.conceptCode,
          conceptName: newTopMatch.conceptName,
          vocabulary: newTopMatch.vocabulary
        },
        aiMetadata: {
          ...currentMapping.aiMetadata,
          confidenceScore: newTopMatch.confidenceScore
        },
        criticHistory: [...(currentMapping.criticHistory || []), iteration]
      };

      return {
        status: 'success',
        omopMapping: updatedMapping
      };
    }

    return {
      status: 'error',
      error: {
        message: 'No alternatives available for re-ranking'
      }
    };
  } catch (error) {
    return {
      status: 'error',
      error: mockAiResponses.apiErrors.serverError
    };
  }
};

/**
 * Validate an OMOP mapping
 *
 * @param {number} parameterId - Parameter ID
 * @param {string} comment - Optional validation comment
 * @returns {Promise<object>} - Validation result
 */
export const validateMapping = async (parameterId, comment = '') => {
  const config = mockAiResponses.validateMapping;

  await delay(config.delay);

  return {
    ...config.successResponse,
    status: 'success',
    timestamp: new Date().toISOString(),
    validatedBy: 'Current User', // TODO: Get from auth context
    comment: comment
  };
};

/**
 * Reject an OMOP mapping
 *
 * @param {number} parameterId - Parameter ID
 * @param {string} reason - Rejection reason
 * @returns {Promise<object>} - Rejection result
 */
export const rejectMapping = async (parameterId, reason = '') => {
  const config = mockAiResponses.rejectMapping;

  await delay(config.delay);

  return {
    ...config.successResponse,
    status: 'success',
    timestamp: new Date().toISOString(),
    rejectedBy: 'Current User', // TODO: Get from auth context
    reason: reason
  };
};

/**
 * Save a manual mapping
 *
 * @param {number} parameterId - Parameter ID
 * @param {object} concept - Selected OMOP concept
 * @returns {Promise<object>} - Save result
 */
export const saveManualMapping = async (parameterId, concept) => {
  const config = mockAiResponses.saveManualMapping;

  await delay(config.delay);

  return {
    ...config.successResponse,
    status: 'success',
    timestamp: new Date().toISOString(),
    mappedBy: 'Current User', // TODO: Get from auth context
    concept: concept
  };
};

/**
 * Search OMOP vocabulary
 *
 * @param {string} query - Search query
 * @param {object} filters - Filter options (vocabulary, domain, etc.)
 * @param {number} page - Page number (1-indexed)
 * @returns {Promise<object>} - Search results
 */
export const searchVocabulary = async (query, filters = {}, page = 1) => {
  const config = mockAiResponses.searchOmopVocabulary;

  await delay(config.delay);

  // Filter vocabulary based on query and filters
  let results = omopVocabulary.concepts.filter(concept => {
    // Text search
    const matchesQuery = !query ||
      concept.conceptName.toLowerCase().includes(query.toLowerCase()) ||
      concept.conceptSynonyms?.some(syn => syn.toLowerCase().includes(query.toLowerCase()));

    // Vocabulary filter
    const matchesVocabulary = !filters.vocabulary || filters.vocabulary === 'all' ||
      concept.vocabulary === filters.vocabulary;

    // Domain filter
    const matchesDomain = !filters.domain || filters.domain === 'all' ||
      concept.domain === filters.domain;

    return matchesQuery && matchesVocabulary && matchesDomain;
  });

  // Pagination
  const totalResults = results.length;
  const totalPages = Math.ceil(totalResults / config.resultsPerPage);
  const startIdx = (page - 1) * config.resultsPerPage;
  const endIdx = startIdx + config.resultsPerPage;
  const paginatedResults = results.slice(startIdx, endIdx);

  return {
    status: 'success',
    results: paginatedResults,
    pagination: {
      currentPage: page,
      totalPages: totalPages,
      totalResults: totalResults,
      resultsPerPage: config.resultsPerPage
    }
  };
};

export default {
  generateAiMapping,
  submitCriticFeedback,
  validateMapping,
  rejectMapping,
  saveManualMapping,
  searchVocabulary
};
